/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
import {
  Box,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  LinearProgress,
  Paper,
  TableHead,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import useAnalytics from '../../../hooks/useAnalytics';
import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import SecondaryButton from '../../../components/utils/SecondaryButton';
import useCSVExport from '../../../hooks/useCSVExport';
import useGuardedExport from '../../../hooks/useGuardedExport';
import { StyledTableCell, StyledTableRow } from '../../../components/utils/StyledTable';

const useStyles = makeStyles((theme) => ({
  headerTable: {
    font: theme.typography.h5.font,
    fontSize: 12,
    color: theme.palette.common.black,
  },
  headerBorderTable: {
    borderBottom: `1.5px solid ${theme.palette.primary.banner}`,
  },
  bottomRightExportButton: {
    paddingRight: 8,
    paddingLeft: 8,
    paddingBottom: 16,
    textAlign: 'right',
    alignSelf: 'flex-end',
    borderRadius: 0,
    width: 138,
  },
  historyTable: {
    borderRadius: 0,
    minWidth: 200,
    whiteSpace: 'nowrap',
    '& tr': {
      height: 48,
      '& td': {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 20,
        paddingRight: 12,
        height: 48,
      },
    },
  },
  separate: {
    borderRadius: 12,
  },
  separateVoidGrid: {
    flexWrap: 'noWrap',
    borderRadius: '12px !important',
  },
  content: {
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 24,
    paddingRight: 24,
    backgroundColor: theme.palette.background.default,
  },
  contentGrid: {
    paddingBottom: 16,
  },
  cardNoMachineTelemetryText: {
    color: theme.palette.primary.text,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: 16,
    textAlign: 'center',
  },
  detailsTitle: {
    fontSize: 14,
    fontWeight: theme.typography.fontWeightMedium,
    paddingRight: '5px',
  },
  headingTitle: {
    fontSize: 16,
    fontWeight: theme.typography.fontWeightMedium,
  },
  machineInfoCardPadding16: {
    padding: '16px !important',
  },
  telemetryTable: {
    borderRadius: 12,
    boxShadow: theme.customShadows.noTopShadow,
  },
  bottomLeftLegend: {
    paddingTop: 4,
  },
  cardNoMachineTelemetry: {
    borderRadius: 12,
  },
  footerGrid: {
    width: 9,
  },
  expandButton: {
    padding: 4,
  },
  settingsContainer: {
    maxWidth: 300,
  },
  jsonContainer: {
    backgroundColor: '#f5f5f5',
    padding: 8,
    borderRadius: 4,
    maxHeight: 200,
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
}));

function DeviceSettingsListItem({
  object, id,
}) {
  const classes = useStyles();
  const elements = Object.entries(object);

  return (
    <StyledTableRow>
      {
        elements.map((entry, index) => (
          <StyledTableCell id={`${id}-${index}`} component="td" className={classes.cellwrap}>
            {entry[1]}
          </StyledTableCell>
        ))
      }
    </StyledTableRow>
  );
}

DeviceSettingsListItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  object: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
};

export default function DeviceSettingsTable({
  search, dataList, totalCount, filters, loading, dateIni, dateEnd, rangeDates,
  page, setPage, rowsPerPage, object, params, id, pagination, paginationTokens, isOverview
}) {
  const { setAnalytics } = useAnalytics();
  const [rawData, setRawData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage || 0);
    search(newPage);
    event.preventDefault();
  };

  // WEBPAGE______________________________
  const classes = useStyles();

  useEffect(() => {
    setRawData(dataList || []);
  }, [dataList]);

  const transformedData = rawData;

  const {
    exportToCSV: baseExportToCSV, loading: exportLoading,
  } = useCSVExport(
    params, filters, dateIni, dateEnd, rangeDates, object,
  );

  const { requestExport, modal: exportModal } = useGuardedExport(
    {
      action: /machinelist|machineassetsdetail|machinecount/i.test(object) ? 'lifeCycleObject' : 'telemetryObject',
      object,
      dateIni,
      dateEnd,
      queryParams: typeof params === 'string' ? params : JSON.stringify(params || {}),
      filters: typeof filters === 'string' ? filters : JSON.stringify(filters || {}),
      totalRows: totalCount,
      title: 'Export device settings to CSV',
    },
    baseExportToCSV,
  );

  // Handler para exportar y enviar analytics
  const handleExportToCSV = () => {
    console.log('Exporting to CSV:', id);
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: isOverview ? 'device_management-overview-telemetry-export_csv' : 'device_management-settings-export_csv',
      },
    });
    requestExport();
  };

  return (
    <Box>
      <Grid
        container
        spacing={0}
        direction="row"
        className={classes.content}
        id="brand-events-table"
      >
        {(rawData !== null) && (Array.isArray(rawData)) && (rawData.length > 0)
          && (
            <Grid
              container
              spacing={0}
              direction="row"
              className={classes.contentGrid}
            >
              <Grid item xs={6} className={classes.bottomLeftLegend}>
                <Typography className={classes.bottomLeftLegend}>Data will be retrieved in real time</Typography>
              </Grid>

              <Grid item xs={6} className={classes.bottomRightExportButton}>
                <span>
                  <SecondaryButton
                    id={`btn${id}Export`}
                    onClick={handleExportToCSV}
                    disabled={exportLoading}
                  >
                    Export .CSV
                  </SecondaryButton>
                </span>
                {exportModal}

              </Grid>

              <Grid
                item
                xs={12}
                className={classes.separate}
              >
                <TableContainer component={Paper} className={classes.telemetryTable}>
                  <Table className={classes.historyTable} aria-label="custom pagination table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell className={classes.tableHead}>Platform Timestamp (UTC)</StyledTableCell>
                        <StyledTableCell className={classes.tableHead}>Device Serial</StyledTableCell>
                        <StyledTableCell className={classes.tableHead}>Settings</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transformedData.map((item, index) => (
                        <React.Fragment key={`${item.serial}-${item.processed_on_utc}`}>
                          <StyledTableRow>
                            <StyledTableCell component="td">{item.processed_on_utc || '-'}</StyledTableCell>
                            <StyledTableCell component="td">{item.serial || '-'}</StyledTableCell>
                            <StyledTableCell component="td" className={classes.settingsContainer}>
                              <Box display="flex" alignItems="center">
                                <IconButton
                                  className={classes.expandButton}
                                  onClick={() => toggleRow(index)}
                                  size="small"
                                >
                                  {expandedRows[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                                <Typography variant="body2">
                                  {expandedRows[index] ? 'Hide Settings' : 'Show Settings'}
                                </Typography>
                              </Box>
                              <Collapse in={expandedRows[index]} timeout="auto" unmountOnExit>
                                <Box className={classes.jsonContainer}>
                                  {item.settings ? JSON.stringify(item.settings, null, 2) : 'No settings data'}
                                </Box>
                              </Collapse>
                            </StyledTableCell>
                          </StyledTableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                    <Grid className={classes.footerGrid}>
                      <TableFooter>
                        <TableRow key="paginationFooter">
                          {pagination === 'limit&offset'
                            ? (
                              <TablePagination
                                rowsPerPageOptions={[20]}
                                colSpan={10}
                                count={totalCount}
                                rowsPerPage={rowsPerPage || 20}
                                page={page}
                                onPageChange={handleChangePage}
                              />
                            ) : (
                              <TablePagination
                                rowsPerPageOptions={[rowsPerPage]}
                                colSpan={10}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                  inputProps: { 'aria-label': 'rows per page' },
                                  native: true,
                                }}
                                onPageChange={handleChangePage} // Explicitly pass the prop
                                ActionsComponent={(props) => (
                                  <TablePaginationActions
                                    {...props}
                                    onChangePage={handleChangePage} // Explicitly pass the prop
                                  />
                                )}
                                labelDisplayedRows={({ from, to, count }) => (((count !== -1 && paginationTokens.length > 1) || rawData.length < 100)
                                  ? `${from}-${to + (page * 100)} of ${count + ((paginationTokens?.length - 1) * 100)}`
                                  : `${from}-${to} of more than ${to}`)}
                                count={
                                  paginationTokens?.length > 1 && rawData?.length === rowsPerPage
                                    ? -1
                                    : rawData?.length
                                }
                              />
                            )}
                        </TableRow>
                      </TableFooter>
                    </Grid>
                  </Table>
                  {loading && <LinearProgress />}
                </TableContainer>
              </Grid>
            </Grid>
          )}
        {(rawData !== null) && (Array.isArray(rawData)) && (rawData.length === 0)
          && !loading
          && (
            <Grid
              item
              xs={12}
              className={classes.separateVoidGrid}
            >
              <Card className={classes.cardNoMachineTelemetry}>
                <CardContent className={classes.machineInfoCardPadding16}>
                  <Typography className={classes.cardNoMachineTelemetryText}>
                    {`No ${id} available`}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
      </Grid>
    </Box>
  );
}
DeviceSettingsTable.propTypes = {
  search: PropTypes.func.isRequired,
  dataList: PropTypes.object,
  totalCount: PropTypes.number,
  filters: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  dateIni: PropTypes.string.isRequired,
  dateEnd: PropTypes.string,
  rangeDates: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  object: PropTypes.string.isRequired,
  params: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  pagination: PropTypes.string,
  paginationTokens: PropTypes.array,
  disableOrderingHeader: PropTypes.bool,
  columnMapping: PropTypes.object,
  columnToHide: PropTypes.array,
  isOverview: PropTypes.bool,

};
DeviceSettingsTable.defaultProps = {
  dataList: [],
  totalCount: 0,
  pagination: 'limit&offset',
  paginationTokens: [],
  dateEnd: null,
  filters: '',
  disableOrderingHeader: false,
  columnMapping: {},
  columnToHide: [],
  isOverview: false
};
